/**
 * The personalized band order rules the form enforces, mirroring
 * api/v1/lib/band-orders.php function for function.
 *
 * The server refuses the same rows; this exists so the member is told before
 * they press Calculate Cost rather than after. bandOrderRules.test.ts pins the
 * same cases api/v1/tests/band-orders-test.php pins.
 *
 * Lauren Gandara (ARPU office), 2026-09-17.
 */

export interface BandRowValues {
  bandLetters: string
  bandFrom: number
  bandTo: number
}

/**
 * "Limit the characters a member can input for band lettering to 16 total
 * (this will include letters, numbers, spaces etc)" - a length cap that counts
 * every character, not a list of allowed characters.
 */
export const BAND_LETTERS_MAX = 16

/** Band lettering as it is counted and stored: outer whitespace removed. */
export function normalizeBandLetters(raw: string): string {
  return (raw ?? '').trim()
}

/**
 * Characters as a person counts them.
 *
 * [...string] iterates code points, so a pasted curly apostrophe or an accented
 * letter counts once instead of by its UTF-16 units.
 */
export function bandLettersLength(raw: string): number {
  return [...normalizeBandLetters(raw)].length
}

/** Trim a typed value to the cap, for the input's own onChange. */
export function capBandLetters(raw: string): string {
  const chars = [...(raw ?? '')]
  return chars.length <= BAND_LETTERS_MAX ? (raw ?? '') : chars.slice(0, BAND_LETTERS_MAX).join('')
}

/** A row the member has not touched at all. */
export function isEmptyBandRow(row: BandRowValues): boolean {
  return normalizeBandLetters(row.bandLetters) === '' && !(row.bandFrom > 0) && !(row.bandTo > 0)
}

/**
 * What is wrong with one row, or null when it is orderable.
 *
 * "Force the member to input the band number (if they have typed something in
 * the 'Band Letters' field) before being able to 'calculate cost' ... if a
 * member forgets to input the band number, then hits calculate cost, they may
 * think its included/ordered" (Lauren, 2026-09-17).
 */
export function bandRowError(row: BandRowValues): string | null {
  const letters = normalizeBandLetters(row.bandLetters)
  const from = Number(row.bandFrom) || 0
  const to = Number(row.bandTo) || 0

  if (letters !== '' && bandLettersLength(letters) > BAND_LETTERS_MAX) {
    return `Band letters can be at most ${BAND_LETTERS_MAX} characters. "${letters}" is ${bandLettersLength(letters)}.`
  }
  if (letters !== '' && (from <= 0 || to <= 0)) {
    return `Enter the band numbers for "${letters}" before calculating the cost.`
  }
  if (letters === '' && (from > 0 || to > 0)) {
    return `Enter the band letters for the band numbers ${from}-${to}.`
  }
  if (letters !== '' && to < from) {
    return `The last band number must not be lower than the first for "${letters}".`
  }
  return null
}

/**
 * The values a duplicate-check verdict belongs to.
 *
 * Comparing this against the row's current values is what stops a verdict
 * earned by one lettering from covering another.
 */
export function bandRowKey(row: BandRowValues): string {
  return `${normalizeBandLetters(row.bandLetters).toUpperCase()}|${Number(row.bandFrom) || 0}|${Number(row.bandTo) || 0}`
}

/** The rows that are actually ordered: everything the member filled in. */
export function orderableBandRows<T extends BandRowValues>(rows: T[]): T[] {
  return rows.filter((row) => !isEmptyBandRow(row))
}
